import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Card,
  CardHeader,
  CardContent,
  Typography,
  Button,
  IconButton,
  Stack,
  Divider,
  Chip,
  alpha,
  useTheme,
  Paper,
} from "@mui/material";
import {
  Close as CloseIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { useQuery, useQueryClient } from "react-query";
import { enqueueSnackbar } from "notistack";
import { useParams } from "react-router-dom";

import { QK } from "../../../../../base/qk";
import {
  getCarePlans,
  getCheckList,
  getClientPaymentServices,
} from "../../../../../api/services/core";
import {
  getVisitByID,
  updateVisit,
} from "../../../../../api/services/scheduling";
import { useUserProfile } from "../../../../../hooks/userProfile";

import CustomInput from "../../../../CustomForm/CustomInput";
import CustomTextArea from "../../../../CustomForm/CustomTextArea";
import CustomCheckbox from "../../../../CustomForm/CustomCheckbox";
import CustomDateField from "../../../../FormFields/CustomDateField";
import CustomTimePicker from "../../../../FormFields/TiimePicker/TimePicker";
import DynamicSelectInput from "../../../../FormFields/CustomSelect/DynamicSelectInput";
import MultiSelect from "../../../../FormFields/CustomSelect/MultiSelect";
import CommonButton from "../../../../CustomForm/CommonButton";
import Loader from "../../../../Loader/Loader";
import Calendar from "./Calendar";
import FundingModal from "../../../Funding/FundingModal";
import { VisitTypes } from "./visit.config";

const UpdateVisitModal = ({ show, handleClose, visitId, clientIdProp }) => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({});
  const [isTimeCritical, setIsTimeCritical] = useState(false);
  const [selectedDaysInMonth, setSelectedDaysInMonth] = useState([]);
  const [isContinueIndifinite, setIsContinueIndifinite] = useState(false);
  const [showFundingModal, setShowFundingModal] = useState(false);
  const [selectedCheckList, setSelectedCheckList] = useState([]);

  const { id } = useParams();
  const clientID = id || clientIdProp;
  const user = useUserProfile();

  const { data: carePlans, isLoading: PlansLoading } = useQuery(
    [QK.carePlans],
    () => getCarePlans()
  );

  const { data: checkList = [] } = useQuery(
    [QK.checkList],
    () => getCheckList()
  );

  const { data: paymentServices } = useQuery(
    [QK.clientPaymentServices, clientID],
    () => getClientPaymentServices(clientID),
    { enabled: !!clientID }
  );

  const { data: visitDetails, isLoading: visitLoading } = useQuery(
    [visitId],
    () => getVisitByID(visitId),
    { enabled: !!visitId }
  );

  useEffect(() => {
    if (visitDetails && Object.keys(visitDetails).length > 0) {
      setFormData({
        visit_name: visitDetails.visit_name || "",
        care_plan: visitDetails.care_plan || null,
        payment_service: visitDetails.payment_service || null,
        carers_required: visitDetails.carers_required || 1,
        personalized_checks: visitDetails.personalized_checks || [],
        checklist: visitDetails.checklist || [],
        support_instructions: visitDetails.support_instructions || "",
        start_time: visitDetails.start_time || "",
        end_time: visitDetails.end_time || null,
        repeat_every: visitDetails.repeat_every || 0,
        recurrence_type: visitDetails.recurrence_type || "",
        selected_days_in_week: visitDetails.selected_days_in_week || [],
        selected_days_in_month: visitDetails.selected_days_in_month || [],
        time_required_from: visitDetails.time_required_from || "",
        time_required_to: visitDetails.time_required_to || "",
        time_critical: visitDetails.time_critical || false,
        time_tolerance: visitDetails.time_tolerance || 0,
        total_duration: visitDetails.total_duration || 0,
        mandatory_notes: visitDetails.mandatory_notes || false,
        allow_photos: visitDetails.allow_photos || false,
        share_with_family: visitDetails.share_with_family || false,
        continue_indefinitely: visitDetails.continue_indefinitely || false,
      });

      setIsTimeCritical(visitDetails.time_critical || false);
      setIsContinueIndifinite(visitDetails.continue_indefinitely || false);

      const formattedSelectedChecks = (
        visitDetails?.checklist_details ?? []
      ).map((item) => ({
        value: item.id,
        label: item.name,
      }));
      setSelectedCheckList(formattedSelectedChecks);
    }
  }, [visitDetails]);

  const totalMinutes =
    formData?.total_duration * formData?.selected_days_in_week?.length;
  const hours = Math.floor((totalMinutes || 0) / 60);
  const minutes = (totalMinutes || 0) % 60;

  const handleChange = (event) => {
    const { name, checked, type, value } = event.target;
    setFormData((prevData) => {
      const updatedData = {
        ...prevData,
        [name]: type === "checkbox" ? checked : value,
      };

      if (name === "continue_indefinitely") {
        updatedData.end_time = null;
      }

      if (name === "time_required_from" || name === "time_required_to") {
        if (updatedData.time_required_from && updatedData.time_required_to) {
          const updatedDuration = calculateTimeDifference(
            updatedData.time_required_from,
            updatedData.time_required_to
          );
          updatedData.total_duration = updatedDuration;
        }
      }

      return updatedData;
    });
  };

  const calculateTimeDifference = (fromTime, toTime) => {
    if (!fromTime || !toTime) return 0;
    const [fromHours, fromMinutes] = fromTime.split(":").map(Number);
    const [toHours, toMinutes] = toTime.split(":").map(Number);
    const fromTimeInMinutes = fromHours * 60 + fromMinutes;
    const toTimeInMinutes = toHours * 60 + toMinutes;
    const diff = toTimeInMinutes - fromTimeInMinutes;
    return diff >= 0 ? diff : 0;
  };

  const handleCheckBox = (name, checked) => {
    if (name === "time_critical") {
      setFormData((prevData) => ({
        ...prevData,
        time_critical: checked,
        time_tolerance: 0,
      }));
    }
    if (name === "continue_indefinitely") {
      setIsContinueIndifinite(checked);
      setFormData((prev) => ({
        ...prev,
        end_time: null,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const requestData = {
      ...formData,
      user: user?.id,
      client: clientID,
      checklist: selectedCheckList?.map((item) => item.value),
    };
    try {
      const response = await updateVisit(visitId, requestData);
      if (response.status === 200) {
        enqueueSnackbar("Visit has been updated.", { variant: "success" });
        queryClient.invalidateQueries([QK.visits, QK.visitPlan]);
        handleClose();
      }
    } catch (error) {
      enqueueSnackbar("An error occurred while updating the Visit.", {
        variant: "error",
      });
    }
  };

  const handleAddInput = () => {
    setFormData((prevData) => ({
      ...prevData,
      personalized_checks: [
        ...(prevData.personalized_checks || []),
        { id: Date.now(), check_name: "" },
      ],
    }));
  };

  const handleRemoveInput = (id) => {
    setFormData((prevData) => ({
      ...prevData,
      personalized_checks: (prevData.personalized_checks || []).filter(
        (check) => check.id !== id
      ),
    }));
  };

  const handlePersonalizedCheck = (index, event) => {
    const value = event.target.value.trim();
    setFormData((prevData) => {
      const newChecks = [...(prevData.personalized_checks || [])];
      if (value || newChecks[index]?.check_name) {
        newChecks[index].check_name = event.target.value;
      }
      return { ...prevData, personalized_checks: newChecks };
    });
  };

  const daysOfWeek = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  const handleVisitTypeChange = (e) => {
    setFormData((prevData) => ({
      ...prevData,
      recurrence_type: e.target.value,
    }));
  };

  const handleDaySelection = (day) => {
    setFormData((prevData) => {
      let updatedDays = [...(prevData.selected_days_in_week || [])];
      if (updatedDays.includes(day)) {
        updatedDays = updatedDays.filter((d) => d !== day);
      } else {
        updatedDays.push(day);
      }
      return {
        ...prevData,
        selected_days_in_week: updatedDays,
      };
    });
  };

  const formattedCheckList = Array.isArray(checkList)
    ? checkList.map((option) => ({
        value: option.id,
        label: option.name,
      }))
    : [];

  const handleSelectChange = (options) => {
    setSelectedCheckList(options);
  };

  if (visitLoading || PlansLoading) {
    return <Loader />;
  }

  return (
    <>
      <Dialog
        open={show}
        onClose={handleClose}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            maxHeight: "90vh",
          },
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: theme.palette.primary.main,
            color: "white",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            py: 2,
            px: 3,
          }}
        >
          <Typography variant="h6" fontWeight={700}>
            Manage Personalised Visits
          </Typography>
          <IconButton onClick={handleClose} sx={{ color: "white" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          <form onSubmit={handleSubmit}>
            <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 3 }}>
              <Box sx={{ flex: 1 }}>
                <Card
                  elevation={0}
                  sx={{
                    border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                    borderRadius: 3,
                    mb: 3,
                  }}
                >
                  <CardHeader
                    title={
                      <Typography variant="h6" fontWeight={700} color="primary">
                        Visit Details
                      </Typography>
                    }
                    sx={{ borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}` }}
                  />
                  <CardContent sx={{ pt: 2 }}>
                    <CustomInput
                      label="Visit Name *"
                      name="visit_name"
                      onChange={handleChange}
                      required
                      value={formData.visit_name || ""}
                      fullWidth
                    />

                    <Box sx={{ mt: 2 }}>
                      <DynamicSelectInput
                        options={carePlans}
                        label="Which care plan is this linked to? *"
                        name="care_plan"
                        onChange={handleChange}
                        required
                        value={formData.care_plan || ""}
                      />
                    </Box>

                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mt: 1 }}>
                      <Box sx={{ flex: 2, minWidth: "200px" }}>
                        <DynamicSelectInput
                          options={paymentServices}
                          labelKey="service"
                          label="Payment Service? *"
                          name="payment_service"
                          onChange={handleChange}
                          required
                          value={formData?.payment_service || ""}
                        />
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={() => setShowFundingModal(true)}
                          sx={{ mt: 1 }}
                        >
                          Add Payment Service
                        </Button>
                      </Box>
                      <Box sx={{ flex: 1, minWidth: "120px" }}>
                        <CustomInput
                          label="Carers required? *"
                          name="carers_required"
                          type="number"
                          defaultValue="1"
                          onChange={handleChange}
                          value={formData.carers_required || 1}
                          fullWidth
                        />
                      </Box>
                    </Box>

                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Please select a checklist:
                      </Typography>
                      <MultiSelect
                        options={formattedCheckList}
                        value={selectedCheckList}
                        onChange={handleSelectChange}
                        placeholder="Select care plan options..."
                      />
                    </Box>

                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Add Personalised checklist:
                      </Typography>
                      {(formData?.personalized_checks || []).map((input, index) => (
                        <Stack direction="row" spacing={1} key={input.id} sx={{ mb: 1 }}>
                          <CustomInput
                            label={`Add Personalised Check ${index + 1}`}
                            name="personalized_check"
                            value={input.check_name || ""}
                            onChange={(event) => handlePersonalizedCheck(index, event)}
                            fullWidth
                          />
                          <IconButton
                            color="error"
                            onClick={() => handleRemoveInput(input.id)}
                            sx={{ mt: 1 }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Stack>
                      ))}
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={handleAddInput}
                      >
                        Add More
                      </Button>
                    </Box>

                    <Box sx={{ mt: 2 }}>
                      <CustomTextArea
                        label="Support Requirements and Instructions"
                        name="support_instructions"
                        onChange={handleChange}
                        rows={3}
                        value={formData.support_instructions || ""}
                        fullWidth
                      />
                    </Box>

                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mt: 1 }}>
                      <Box sx={{ flex: 2, minWidth: "200px" }}>
                        <CustomDateField
                          name="start_time"
                          label="When do you want to start the visit?"
                          type="datetime-local"
                          onChange={handleChange}
                          required
                          value={formData.start_time || ""}
                        />
                      </Box>
                      <Box sx={{ flex: 1, minWidth: "150px" }}>
                        <CustomCheckbox
                          label="Continue Indefinitely"
                          name="continue_indefinitely"
                          isInitiallyChecked={formData.continue_indefinitely || false}
                          onClick={(checked) =>
                            handleCheckBox("continue_indefinitely", checked)
                          }
                        />
                      </Box>
                    </Box>

                    {!isContinueIndifinite && (
                      <Box sx={{ mt: 2 }}>
                        <CustomDateField
                          name="end_time"
                          label="When do you want to end the visit? *"
                          value={formData.end_time || ""}
                          type="datetime-local"
                          onChange={handleChange}
                        />
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Box>

              <Box sx={{ flex: 1 }}>
                <Card
                  elevation={0}
                  sx={{
                    border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                    borderRadius: 3,
                    mb: 3,
                  }}
                >
                  <CardHeader
                    title={
                      <Typography variant="h6" fontWeight={700} color="primary">
                        When do you want to schedule the visit?
                      </Typography>
                    }
                    sx={{ borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}` }}
                  />
                  <CardContent sx={{ pt: 2 }}>
                    <DynamicSelectInput
                      label="How often do you want to schedule the visits?"
                      labelKey="label"
                      name="recurrence_type"
                      type="number"
                      options={VisitTypes}
                      onChange={handleVisitTypeChange}
                      value={formData?.recurrence_type || ""}
                    />

                    {formData?.recurrence_type === 2 && (
                      <Box sx={{ mt: 2, textAlign: "center" }}>
                        <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
                          Select Days
                        </Typography>
                        <Stack direction="row" spacing={0.5} justifyContent="center" sx={{ mt: 1 }}>
                          {daysOfWeek.map((day) => (
                            <Chip
                              key={day}
                              label={day}
                              onClick={() => handleDaySelection(day)}
                              color={
                                formData?.selected_days_in_week?.includes(day)
                                  ? "primary"
                                  : "default"
                              }
                              variant={
                                formData?.selected_days_in_week?.includes(day)
                                  ? "filled"
                                  : "outlined"
                              }
                              sx={{ cursor: "pointer" }}
                            />
                          ))}
                        </Stack>
                      </Box>
                    )}

                    {formData?.recurrence_type === 3 && (
                      <Box sx={{ mt: 2 }}>
                        <Calendar
                          visitType="Monthly"
                          onSelectDays={(days) => setSelectedDaysInMonth(days)}
                        />
                      </Box>
                    )}

                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mt: 1 }}>
                      <Box sx={{ flex: 1, minWidth: "150px" }}>
                        <CustomCheckbox
                          label="Time Critical?"
                          name="time_critical"
                          onClick={(checked) =>
                            handleCheckBox("time_critical", checked)
                          }
                          isInitiallyChecked={formData?.time_critical || false}
                        />
                      </Box>
                      <Box sx={{ flex: 1, minWidth: "150px" }}>
                        <CustomInput
                          label="Time Tolerance (Minutes)"
                          name="time_tolerance"
                          type="number"
                          defaultValue="60"
                          onChange={handleChange}
                          value={formData?.time_tolerance || 0}
                          fullWidth
                        />
                      </Box>
                    </Box>

                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mt: 1 }}>
                      <Box sx={{ flex: 1, minWidth: "150px" }}>
                        <CustomTimePicker
                          label="Time Required From"
                          name="time_required_from"
                          onChange={handleChange}
                          required
                          value={formData.time_required_from || ""}
                        />
                      </Box>
                      <Box sx={{ flex: 1, minWidth: "150px" }}>
                        <CustomTimePicker
                          label="Time Required To"
                          name="time_required_to"
                          onChange={handleChange}
                          required
                          value={formData.time_required_to || ""}
                        />
                      </Box>
                      <Box sx={{ flex: 1, minWidth: "150px" }}>
                        <CustomInput
                          label="Total Time Required (Minutes)"
                          name="total_duration"
                          type="number"
                          value={formData.total_duration || 0}
                          fullWidth
                        />
                      </Box>
                      <Box sx={{ flex: 1, minWidth: "150px" }}>
                        <Paper
                          variant="outlined"
                          sx={{
                            p: 2,
                            textAlign: "center",
                            bgcolor: alpha(theme.palette.primary.main, 0.04),
                            borderColor: alpha(theme.palette.primary.main, 0.2),
                            mt: 1,
                          }}
                        >
                          <Typography variant="body2" fontWeight={700} color="primary">
                            Weekly Hours: {hours} H & {minutes} Mins
                          </Typography>
                        </Paper>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>

                <Card
                  elevation={0}
                  sx={{
                    border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                    borderRadius: 3,
                  }}
                >
                  <CardHeader
                    title={
                      <Typography variant="h6" fontWeight={700} color="primary">
                        Additional Details
                      </Typography>
                    }
                    sx={{ borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}` }}
                  />
                  <CardContent sx={{ pt: 2 }}>
                    <Stack spacing={1.5}>
                      <CustomCheckbox
                        label="Are photos allowed for this action?"
                        name="allow_photos"
                        isInitiallyChecked={formData.allow_photos || false}
                        onClick={(checked) =>
                          handleCheckBox("allow_photos", checked)
                        }
                      />
                      <CustomCheckbox
                        label="Share with Family"
                        name="share_with_family"
                        isInitiallyChecked={formData.share_with_family || false}
                        onClick={(checked) =>
                          handleCheckBox("share_with_family", checked)
                        }
                      />
                      <CustomCheckbox
                        label="Mandatory Notes"
                        name="mandatory_notes"
                        isInitiallyChecked={formData.mandatory_notes || false}
                        onClick={(checked) =>
                          handleCheckBox("mandatory_notes", checked)
                        }
                      />
                    </Stack>
                  </CardContent>
                </Card>
              </Box>
            </Box>
          </form>
        </DialogContent>

        <DialogActions sx={{ p: 3, borderTop: `1px solid ${alpha(theme.palette.divider, 0.08)}` }}>
          <CommonButton onSubmit={handleSubmit} onClose={handleClose} />
        </DialogActions>
      </Dialog>

      <FundingModal
        show={showFundingModal}
        clientID={visitDetails?.client}
        onHide={() => setShowFundingModal(false)}
      />
    </>
  );
};

export default UpdateVisitModal;